/**
 * A section card with a heading, a subline and a strip of summary figures.
 *
 * WHY ONE COMPONENT FOR BOTH SIDES
 * --------------------------------
 * The tenant and landlord panels are structurally identical — heading, subline,
 * a row of stats, then (in K3b) a table. Building them twice would let the two
 * drift apart in spacing and state handling for no reason. What must NOT be
 * shared is the DATA, and it is not: each caller passes its own already-selected
 * figures, and this component never reaches into a payload or computes anything.
 *
 * It also carries the three states a panel can be in. `error` shows the retry and
 * suppresses the stats rather than rendering a row of dashes that reads like real
 * zeroes; `loading` shows skeletons; `empty` explains an honest nothing instead of
 * leaving a blank card.
 */
import React from "react";

import { Skeleton } from "../../../components/ui/skeleton";

export default function DepositsSummaryCard({
  title,
  subline,
  stats = [],
  loading = false,
  error = "",
  onRetry,
  emptyMessage,
  isEmpty = false,
  testId,
  children,
}) {
  return (
    <section
      data-testid={testId}
      aria-busy={loading || undefined}
      className="flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h2 className="text-[14px] font-semibold leading-tight text-card-foreground">
        {title}
      </h2>
      <p className="mt-1 text-[12px] leading-snug text-muted-foreground">{subline}</p>

      <div className="mt-3.5">
        {error ? (
          <div
            data-testid={testId ? `${testId}-error` : undefined}
            className="rounded-lg border border-border bg-muted/40 p-3.5"
          >
            {/* Text, never colour alone. */}
            <p className="m-0 text-[12px] text-destructive">{error}</p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              >
                Erneut laden
              </button>
            ) : null}
          </div>
        ) : loading ? (
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3.5 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i}>
                <Skeleton className="h-5 w-20" />
                <Skeleton className="mt-1.5 h-3 w-16" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div
            data-testid={testId ? `${testId}-empty` : undefined}
            className="rounded-lg border border-dashed border-border p-5 text-center text-[12px] text-muted-foreground"
          >
            {emptyMessage}
          </div>
        ) : (
          <dl
            data-testid={testId ? `${testId}-stats` : undefined}
            className="grid grid-cols-2 gap-x-3 gap-y-3.5 rounded-lg border border-border p-3.5 sm:grid-cols-4"
          >
            {stats.map((s) => (
              <div key={s.label} data-testid={s.testId}>
                <dd
                  className={`text-[16px] font-semibold leading-tight ${
                    s.emphasis
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-card-foreground"
                  }`}
                >
                  {s.value}
                </dd>
                <dt className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  {s.label}
                </dt>
              </div>
            ))}
          </dl>
        )}
      </div>

      {children}
    </section>
  );
}
